import { Row, Col, Card, Statistic, Table, Tag, Space, Progress, Spin } from 'antd'
import {
  MailOutlined,
  TwitterOutlined,
  SendOutlined,
  MessageOutlined,
  RiseOutlined,
  FallOutlined,
  UserAddOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons'
import { Area, Pie } from '@ant-design/charts'
import dayjs from 'dayjs'
import { useDashboardStats, useDailyStats, useFunnelStats } from '../hooks'
import type { AccountState, OperationLog } from '../types'

function Dashboard() {
  const { data: dashboardStats, isLoading: statsLoading } = useDashboardStats()
  const { data: dailyStats, isLoading: dailyLoading } = useDailyStats({
    start_date: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    end_date: dayjs().format('YYYY-MM-DD'),
  })
  const { data: funnelData } = useFunnelStats()

  if (statsLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    )
  }

  const today = dashboardStats?.today
  const week = dashboardStats?.week
  const pipeline = dashboardStats?.pipeline

  // 趋势图配置
  const trendData = dailyStats ? [
    ...(dailyStats?.map((d: any) => ({
      date: d.date,
      value: (d.emails_sent || 0) + (d.twitter_dms_sent || 0) + (d.telegram_sent || 0),
      type: '发送量',
    })) || []),
    ...(dailyStats?.map((d: any) => ({
      date: d.date,
      value: d.total_replies || 0,
      type: '回复量',
    })) || [])
  ] : []
  
  const trendConfig = {
    data: trendData,
    xField: 'date',
    yField: 'value',
    seriesField: 'type',
    smooth: true,
    animation: {
      appear: {
        animation: 'path-in',
        duration: 1000,
      },
    },
  }

  // 渠道分布饼图
  const channelData = [
    { type: 'Email', value: today?.emails_sent || 0 },
    { type: 'Twitter', value: today?.twitter_sent || 0 },
    { type: 'Telegram', value: today?.telegram_sent || 0 },
  ]
  
  const totalSent = channelData.reduce((sum, item) => sum + item.value, 0)
  
  const channelPieConfig = {
    data: channelData,
    angleField: 'value',
    colorField: 'type',
    radius: 0.8,
    innerRadius: 0.6,
    label: totalSent > 0 ? {
      type: 'inner',
      offset: '-50%',
      content: (datum: any) => datum?.value?.toString() || '0',
      style: {
        textAlign: 'center',
        fontSize: 14,
      },
    } : false,
    interactions: [{ type: 'element-active' }],
    statistic: {
      title: {
        content: '今日发送',
      },
      content: {
        content: `${totalSent}`,
      },
    },
  }

  // 账号状态列
  const accountColumns = [
    {
      title: '账号',
      dataIndex: 'account_name',
      key: 'account_name',
      ellipsis: true,
    },
    {
      title: '类型',
      dataIndex: 'account_type',
      key: 'account_type',
      render: (type: string) => {
        const typeMap: Record<string, { color: string; icon: React.ReactNode }> = {
          email: { color: 'blue', icon: <MailOutlined /> },
          twitter: { color: 'cyan', icon: <TwitterOutlined /> },
          telegram: { color: 'geekblue', icon: <SendOutlined /> },
        }
        return (
          <Tag icon={typeMap[type]?.icon} color={typeMap[type]?.color}>
            {type.toUpperCase()}
          </Tag>
        )
      },
    },
    {
      title: '健康状态',
      dataIndex: 'health',
      key: 'health',
      render: (health: string) => {
        const healthMap: Record<string, { color: string; text: string }> = {
          healthy: { color: 'success', text: '正常' },
          warning: { color: 'warning', text: '警告' },
          restricted: { color: 'error', text: '受限' },
          banned: { color: 'default', text: '封禁' },
        }
        return <Tag color={healthMap[health]?.color}>{healthMap[health]?.text}</Tag>
      },
    },
    {
      title: '今日使用',
      key: 'usage',
      render: (_: unknown, record: AccountState) => (
        <Progress
          percent={Math.round((record.daily_usage / record.daily_limit) * 100)}
          size="small"
          status={record.daily_usage >= record.daily_limit ? 'exception' : 'normal'}
          format={() => `${record.daily_usage}/${record.daily_limit}`}
        />
      ),
    },
  ]

  // 最近活动列
  const activityColumns = [
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      render: (time: string) => dayjs(time).format('HH:mm:ss'),
    },
    {
      title: '类型',
      dataIndex: 'category',
      key: 'category',
      width: 80,
      render: (category: string) => <Tag>{category}</Tag>,
    },
    {
      title: '消息',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
    },
  ]

  return (
    <div className="fade-in">
      {/* 页面标题 */}
      <div className="page-header">
        <h1>仪表盘</h1>
        <span style={{ color: 'rgba(0,0,0,0.45)' }}>
          更新时间：{dayjs().format('YYYY-MM-DD HH:mm:ss')}
        </span>
      </div>

      {/* 今日统计 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card className="stat-card">
            <Statistic
              title="今日发送"
              value={today?.total_sent || 0}
              prefix={<SendOutlined style={{ color: '#1890ff' }} />}
              suffix={
                <span className="stat-trend up">
                  <RiseOutlined /> 12%
                </span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="stat-card">
            <Statistic
              title="今日回复"
              value={today?.replies || 0}
              prefix={<MessageOutlined style={{ color: '#52c41a' }} />}
              suffix={
                <span className="stat-trend up">
                  <RiseOutlined /> 8%
                </span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="stat-card">
            <Statistic
              title="意向客户"
              value={today?.interested || 0}
              prefix={<UserAddOutlined style={{ color: '#faad14' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="stat-card">
            <Statistic
              title="会议预约"
              value={today?.meetings || 0}
              prefix={<CheckCircleOutlined style={{ color: '#722ed1' }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* 周统计 & 漏斗 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={12}>
          <Card title="本周概览" size="small">
            <Row gutter={16}>
              <Col span={12}>
                <Statistic title="总发送" value={week?.total_sent || 0} />
              </Col>
              <Col span={12}>
                <Statistic title="总回复" value={week?.total_replies || 0} />
              </Col>
              <Col span={12}>
                <Statistic
                  title="回复率"
                  value={week?.reply_rate || 0}
                  suffix="%"
                  precision={1}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="转化率"
                  value={week?.conversion_rate || 0}
                  suffix="%"
                  precision={1}
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="项目管道" size="small">
            <Space wrap>
              <Tag color="blue">新项目: {pipeline?.new || 0}</Tag>
              <Tag color="cyan">已评分: {pipeline?.scored || 0}</Tag>
              <Tag color="geekblue">待触达: {pipeline?.queued || 0}</Tag>
              <Tag color="purple">Phase1: {pipeline?.phase1 || 0}</Tag>
              <Tag color="magenta">Phase2: {pipeline?.phase2 || 0}</Tag>
              <Tag color="orange">Phase3: {pipeline?.phase3 || 0}</Tag>
              <Tag color="green">已回复: {pipeline?.replied || 0}</Tag>
              <Tag color="gold">有意向: {pipeline?.interested || 0}</Tag>
              <Tag color="lime">已转化: {pipeline?.converted || 0}</Tag>
              <Tag color="default">冷池: {pipeline?.cold_pool || 0}</Tag>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* 趋势图 & 渠道分布 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={16}>
          <Card title="30天趋势" size="small" loading={dailyLoading}>
            <Area {...trendConfig} height={250} />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="今日渠道分布" size="small">
            <Pie {...channelPieConfig} height={250} />
          </Card>
        </Col>
      </Row>

      {/* 账号状态 & 最近活动 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card title="账号状态" size="small">
            <Table
              dataSource={dashboardStats?.accounts}
              columns={accountColumns}
              rowKey="id"
              size="small"
              pagination={false}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="最近活动" size="small">
            <Table
              dataSource={dashboardStats?.recent_activities?.slice(0, 10)}
              columns={activityColumns}
              rowKey="id"
              size="small"
              pagination={false}
            />
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default Dashboard
